import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Host for PDP content blocks that resolve a metafield variant-first, then product.
 *
 * Liquid sets `data-content-key` to `variant-{id}`, `product` or `none` — whichever
 * level supplied the content. On `variant:update` the host copies the server-rendered
 * block only when that key changes, so switching between variants that share the
 * product fallback does not reset playing videos, sliders or countdowns.
 *
 * Copy innerHTML — do not morph or replaceWith nodes from event.detail.data.html.
 * Scripts are re-created after the copy: a block that rendered nothing on first load
 * never loaded its module, and innerHTML does not execute scripts.
 */
class VariantContentSync extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#scope = null;
  }

  /**
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const variantUpdate = /** @type {VariantUpdateEvent} */ (event);
    const doc = variantUpdate.detail?.data?.html;
    if (!doc || typeof doc.querySelector !== 'function') return;

    if (variantUpdate.detail.data.newProduct) {
      this.dataset.productId = variantUpdate.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const next = doc.querySelector(`variant-content-sync[data-block-id="${this.dataset.blockId}"]`);
    if (!(next instanceof HTMLElement)) return;
    if (next.dataset.contentKey === this.dataset.contentKey) return;

    this.dataset.contentKey = next.dataset.contentKey;
    this.innerHTML = next.innerHTML;
    activateScripts(this);
  };
}

/**
 * @param {ParentNode} root
 */
const activateScripts = (root) => {
  for (const script of root.querySelectorAll('script')) {
    const clone = document.createElement('script');

    for (const { name, value } of script.attributes) {
      clone.setAttribute(name, value);
    }

    clone.textContent = script.textContent;
    script.replaceWith(clone);
  }
};

if (!customElements.get('variant-content-sync')) {
  customElements.define('variant-content-sync', VariantContentSync);
}
